import { EnrollmentPaymentsRow, EnrollmentsRow } from "@outschool/db-queries";
import {
  CouponType,
  EnrollmentPayment,
  TenderType
} from "@outschool/gql-backend-generated";
import { CurrencyCode } from "@outschool/gql-frontend-generated";
import * as Time from "@outschool/time";
import { dayjs } from "@outschool/time";
import _ from "lodash";

import * as EnrollmentPaymentRefund from "./EnrollmentPaymentRefund";
import { EnrollmentPaymentFragmentFragment } from "./generated/graphql";

export function isPaidThroughStripe(
  enrollmentPayment: EnrollmentPaymentsRow
): boolean {
  return !!(enrollmentPayment && enrollmentPayment.stripe_charge_id);
}

type IsCharterPurchaseOrderType = Pick<
  EnrollmentPaymentsRow,
  "purchase_order_id"
>;

export function isCharterPurchaseOrder(
  enrollmentPayment: IsCharterPurchaseOrderType
): boolean {
  return !!(enrollmentPayment && enrollmentPayment.purchase_order_id);
}

type IsPaidByCharterAdminType = IsCharterPurchaseOrderType & {
  organization_uid?: EnrollmentPaymentsRow["organization_uid"];
  organization?: { uid: string } | null;
};

export function isPaidByCharterAdmin(
  enrollmentPayment: IsPaidByCharterAdminType
): boolean {
  return (
    !!enrollmentPayment &&
    !isCharterPurchaseOrder(enrollmentPayment) &&
    !!(enrollmentPayment.organization || !!enrollmentPayment.organization_uid)
  );
}

export function isCharterOrder(
  enrollmentPayment: IsPaidByCharterAdminType & IsCharterPurchaseOrderType
): boolean {
  return (
    isCharterPurchaseOrder(enrollmentPayment) ||
    isPaidByCharterAdmin(enrollmentPayment)
  );
}

export function isESAOrder(
  enrollmentPayment: Pick<EnrollmentPaymentsRow, "esa_order_id"> & {
    credit_account_type?: string | null;
  }
): boolean {
  return !!(
    enrollmentPayment &&
    (enrollmentPayment.esa_order_id ||
      enrollmentPayment.credit_account_type === "esa_membership")
  );
}

export function isBulkPrepaidEnrollmentPayment(
  enrollmentPayment: EnrollmentPaymentsRow
) {
  return isESAOrder(enrollmentPayment) || isCharterOrder(enrollmentPayment);
}

export function isInternationalPayment(enrollmentPayment?: {
  currency_code?: CurrencyCode | null;
}) {
  return (
    !!enrollmentPayment?.currency_code &&
    enrollmentPayment?.currency_code !== CurrencyCode.Usd
  );
}

export function isStoredCredit(enrollmentPayment: EnrollmentPaymentsRow) {
  return Boolean(enrollmentPayment?.credit_transaction_uid);
}

export function purchaseId(enrollmentPayment: {
  order_slug_id?: string;
  stripe_charge_id?: string;
  paid_at?: Date;
}): string {
  return (
    enrollmentPayment &&
    (enrollmentPayment.order_slug_id ||
      enrollmentPayment.stripe_charge_id ||
      dayjs(enrollmentPayment.paid_at)
        .startOf("minute")
        .format("YYYYMMDDHHmmSS"))
  );
}

export function totalUnrefundedCents(
  enrollmentPayments: (
    | EnrollmentPaymentsRow
    | EnrollmentPaymentFragmentFragment
  )[],
  enrollmentPaymentRefunds: { amount_cents?: number }[] | null
): number {
  return (
    totalPaidCents(enrollmentPayments) -
    EnrollmentPaymentRefund.totalCents(enrollmentPaymentRefunds)
  );
}

export function totalPaidCents<T extends { amount_cents?: number | null }>(
  enrollmentPayments: T[]
): number {
  return enrollmentPayments.reduce(
    (total, enrollmentPayment) => total + (enrollmentPayment.amount_cents ?? 0),
    0
  );
}

export function totalPaidMinorCurrency<
  T extends {
    amount_cents: number;
    amount_in_local_currency: number | null;
  }
>(enrollmentPayments: T[]): number {
  return enrollmentPayments.reduce(
    (total, enrollmentPayment) =>
      total +
      (enrollmentPayment.amount_in_local_currency ??
        enrollmentPayment.amount_cents ??
        0),
    0
  );
}

export function totalTeacherDiscountCents<
  T extends {
    amount_cents?: number;
    couponCode?: { coupon?: { couponType: CouponType } };
  }
>(enrollmentPayments: T[]): number {
  return enrollmentPayments.reduce(
    (total, enrollmentPayment) =>
      enrollmentPayment.couponCode?.coupon?.couponType === CouponType.Teacher
        ? total + (enrollmentPayment.amount_cents ?? 0)
        : total,
    0
  );
}

export function isTeacherCouponPayment<
  T extends {
    couponCode?: { coupon?: { couponType: CouponType } };
  }
>(enrollmentPayment: T) {
  return (
    enrollmentPayment.couponCode?.coupon?.couponType === CouponType.Teacher
  );
}

export function isOutschoolCouponPayment<
  T extends {
    couponCode?: { coupon?: { couponType: CouponType } };
  }
>(enrollmentPayment: T) {
  return (
    enrollmentPayment.couponCode?.coupon?.couponType === CouponType.Outschool
  );
}

export function unrefundedCents(
  enrollmentPayment: EnrollmentPaymentsRow,
  enrollmentPaymentRefunds: $TSFixMe
): number {
  return Math.max(
    enrollmentPayment.amount_cents -
      EnrollmentPaymentRefund.totalCents(enrollmentPaymentRefunds),
    0
  );
}

export function unrefundedAmount(
  enrollmentPayment: EnrollmentPaymentsRow,
  enrollmentPaymentRefunds: $TSFixMe
): number {
  return (
    (enrollmentPayment.amount_in_local_currency ?? 0) -
    EnrollmentPaymentRefund.totalAmount(enrollmentPaymentRefunds)
  );
}

export function periodString(enrollmentPayment: EnrollmentPaymentsRow): string {
  return Time.briefDurationStringWithoutTimes(
    enrollmentPayment.start_time,
    enrollmentPayment.end_time,
    "UTC"
  );
}

export function paymentMethodName(
  enrollmentPayment: Pick<
    EnrollmentPayment,
    | "stripe_charge_id"
    | "last4"
    | "purchase_order_id"
    | "voucher_name"
    | "credit_transaction_uid"
    | "coupon_code_uid"
    | "esa_order_id"
    | "invoice_uid"
    | "funding_program_invoice_uid"
  > & { credit_account_type?: string | null }
): TenderType | null {
  // TODO: check something better than last4
  if (!!enrollmentPayment.stripe_charge_id || !!enrollmentPayment.last4) {
    return TenderType.Stripe;
  } else if (!!enrollmentPayment.purchase_order_id) {
    return TenderType.PurchaseOrder;
  } else if (!!enrollmentPayment.voucher_name) {
    return TenderType.PricingOffer;
  } else if (enrollmentPayment.credit_account_type === "subscription") {
    return TenderType.SubscriptionCredit;
  } else if (enrollmentPayment.credit_account_type === "esa_membership") {
    return TenderType.EsaMembershipCredit;
  } else if (!!enrollmentPayment.credit_transaction_uid) {
    return TenderType.StoredCredit;
  } else if (!!enrollmentPayment.coupon_code_uid) {
    return TenderType.Coupon;
  } else if (!!enrollmentPayment.esa_order_id) {
    return TenderType.ClassWallet;
  } else if (enrollmentPayment.funding_program_invoice_uid) {
    return TenderType.FundingProgramInvoice;
  } else {
    return null;
  }
}
export function groupByPaymentMethod<
  T extends EnrollmentPaymentFragmentFragment
>(enrollmentPayments: T[]): Array<T[]> {
  return Object.values(
    _.groupBy(enrollmentPayments, ep => {
      const tenderType = paymentMethodName(ep);
      if (!tenderType) {
        throw new Error(
          `Unknown payment method for enrollment payment ${ep.uid}`
        );
      } else if (tenderType === TenderType.PricingOffer) {
        return ep.voucher_name;
      } else {
        return tenderType;
      }
    })
  );
}

export function isPaymentForFirstWeek(
  enrollmentPayment: EnrollmentPaymentsRow,
  enrollment: EnrollmentsRow
): boolean {
  return enrollmentPayment.order_slug_id === enrollment.order_slug_id;
}
